import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Calendar, User, MapPin, 
  Maximize2, Minimize2, ArrowLeftRight, Layers
} from 'lucide-react';
import { Button } from '../ui/button';

/**
 * BeforeAfterComparison Component
 * 
 * Provides three comparison modes:
 * 1. Side-by-side: Before and after photos displayed next to each other
 * 2. Slider: Interactive slider to reveal before/after
 * 3. Toggle: Click/tap to switch between before and after
 */
const BeforeAfterComparison = ({ 
  beforePhotos = [], 
  afterPhotos = [], 
  booking = {},
  onClose 
}) => {
  const [comparisonMode, setComparisonMode] = useState('sidebyside'); // sidebyside, slider, toggle
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [showAfter, setShowAfter] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const sliderRef = useRef(null);
  const isDragging = useRef(false);

  // Get photo pairs (match before[i] with after[i])
  const maxPairs = Math.max(beforePhotos.length, afterPhotos.length);
  const pairs = Array.from({ length: maxPairs }, (_, i) => ({
    before: beforePhotos[i] || null,
    after: afterPhotos[i] || null
  }));

  const currentPair = pairs[currentPairIndex] || { before: null, after: null };

  // Handle slider drag
  const handleMouseDown = (e) => {
    isDragging.current = true;
    updateSliderPosition(e);
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    updateSliderPosition(e);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleTouchStart = (e) => {
    isDragging.current = true;
    updateSliderPosition(e.touches[0]);
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    updateSliderPosition(e.touches[0]);
  };

  const updateSliderPosition = (e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchend', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  const navigatePair = (direction) => {
    if (direction === 'prev') {
      setCurrentPairIndex(prev => prev > 0 ? prev - 1 : pairs.length - 1);
    } else {
      setCurrentPairIndex(prev => prev < pairs.length - 1 ? prev + 1 : 0);
    }
    setSliderPosition(50);
    setShowAfter(false);
  };

  if (!beforePhotos.length && !afterPhotos.length) {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl mb-4">No before/after photos available for this booking</p>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 bg-black/95 z-50 flex flex-col ${isFullscreen ? '' : 'p-4'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div>
          <h2 className="text-xl font-bold text-white font-['Cormorant_Garamond']">
            Before & After Comparison
          </h2>
          <div className="flex items-center gap-4 mt-1 text-sm text-white/60">
            {booking.service_name && (
              <span>{booking.service_name}</span>
            )}
            {booking.scheduled_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(booking.scheduled_date).toLocaleDateString()}
              </span>
            )}
            {booking.address && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {booking.address}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Mode Selector */}
          <div className="flex items-center bg-white/10 rounded-lg p-1">
            <button
              onClick={() => setComparisonMode('sidebyside')}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                comparisonMode === 'sidebyside' 
                  ? 'bg-[#D4AF37] text-black' 
                  : 'text-white/70 hover:text-white'
              }`}
              title="Side by Side"
            >
              <Layers className="w-4 h-4" />
            </button>
            <button
              onClick={() => setComparisonMode('slider')}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                comparisonMode === 'slider' 
                  ? 'bg-[#D4AF37] text-black' 
                  : 'text-white/70 hover:text-white'
              }`}
              title="Slider"
            >
              <ArrowLeftRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setComparisonMode('toggle')}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                comparisonMode === 'toggle' 
                  ? 'bg-[#D4AF37] text-black' 
                  : 'text-white/70 hover:text-white'
              }`}
              title="Toggle"
            >
              Toggle
            </button>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="text-white/70 hover:text-white"
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white/70 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 relative">
        {/* Navigation Arrows */}
        {pairs.length > 1 && (
          <>
            <Button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70"
              variant="ghost"
              onClick={() => navigatePair('prev')}
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </Button>
            <Button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70"
              variant="ghost"
              onClick={() => navigatePair('next')}
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </Button>
          </>
        )}

        {/* Comparison View */}
        <div className={`w-full max-w-6xl ${isFullscreen ? 'h-full' : 'max-h-[70vh]'}`}>
          
          {/* Side by Side Mode */}
          {comparisonMode === 'sidebyside' && (
            <div className="flex gap-4 h-full">
              <div className="flex-1 relative">
                <div className="absolute top-2 left-2 z-10 bg-blue-500/80 text-white px-3 py-1 rounded-full text-sm font-medium">
                  BEFORE
                </div>
                {currentPair.before ? (
                  <img
                    src={currentPair.before.file_url}
                    alt="Before"
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/5 rounded-lg">
                    <p className="text-white/40">No before photo</p>
                  </div>
                )}
              </div>
              <div className="flex-1 relative">
                <div className="absolute top-2 left-2 z-10 bg-green-500/80 text-white px-3 py-1 rounded-full text-sm font-medium">
                  AFTER
                </div>
                {currentPair.after ? (
                  <img
                    src={currentPair.after.file_url}
                    alt="After"
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/5 rounded-lg">
                    <p className="text-white/40">No after photo</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Slider Mode */}
          {comparisonMode === 'slider' && (
            <div 
              ref={sliderRef}
              className="relative w-full h-full cursor-col-resize select-none rounded-lg overflow-hidden"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
            >
              {/* After Image (bottom layer) */}
              <div className="absolute inset-0">
                {currentPair.after ? (
                  <img
                    src={currentPair.after.file_url}
                    alt="After"
                    className="w-full h-full object-contain"
                    draggable={false}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/5">
                    <p className="text-white/40">No after photo</p>
                  </div>
                )}
                <div className="absolute top-2 right-2 z-10 bg-green-500/80 text-white px-3 py-1 rounded-full text-sm font-medium">
                  AFTER
                </div>
              </div>

              {/* Before Image (top layer, clipped) */}
              <div 
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${sliderPosition}%` }}
              >
                {currentPair.before ? (
                  <img
                    src={currentPair.before.file_url}
                    alt="Before"
                    className="w-full h-full object-contain"
                    style={{ 
                      width: sliderRef.current ? `${sliderRef.current.offsetWidth}px` : '100%',
                      maxWidth: 'none'
                    }}
                    draggable={false}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/10">
                    <p className="text-white/40">No before photo</p>
                  </div>
                )}
                <div className="absolute top-2 left-2 z-10 bg-blue-500/80 text-white px-3 py-1 rounded-full text-sm font-medium">
                  BEFORE
                </div>
              </div>

              {/* Slider Handle */}
              <div 
                className="absolute top-0 bottom-0 w-1 bg-[#D4AF37] cursor-col-resize z-20"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-lg">
                  <ArrowLeftRight className="w-5 h-5 text-black" />
                </div>
              </div>
            </div>
          )}

          {/* Toggle Mode */}
          {comparisonMode === 'toggle' && (
            <div 
              className="relative w-full h-full cursor-pointer rounded-lg overflow-hidden"
              onClick={() => setShowAfter(!showAfter)}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={showAfter ? 'after' : 'before'}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-full"
                >
                  {showAfter ? (
                    <>
                      {currentPair.after ? (
                        <img
                          src={currentPair.after.file_url}
                          alt="After"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-white/5">
                          <p className="text-white/40">No after photo</p>
                        </div>
                      )}
                      <div className="absolute top-2 left-2 z-10 bg-green-500/80 text-white px-3 py-1 rounded-full text-sm font-medium">
                        AFTER
                      </div>
                    </>
                  ) : (
                    <>
                      {currentPair.before ? (
                        <img
                          src={currentPair.before.file_url}
                          alt="Before"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-white/5">
                          <p className="text-white/40">No before photo</p>
                        </div>
                      )}
                      <div className="absolute top-2 left-2 z-10 bg-blue-500/80 text-white px-3 py-1 rounded-full text-sm font-medium">
                        BEFORE
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
              
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
                Click to toggle between before and after
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer - Photo pair indicator */}
      {pairs.length > 1 && (
        <div className="p-4 border-t border-white/10 flex items-center justify-center gap-2">
          {pairs.map((pair, index) => (
            <button
              key={`pair-nav-${pair.id || index}`}
              onClick={() => setCurrentPairIndex(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentPairIndex ? 'bg-[#D4AF37]' : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
          <span className="ml-4 text-white/60 text-sm">
            {currentPairIndex + 1} of {pairs.length} pairs
          </span>
        </div>
      )}

      {/* Photo Info */}
      <div className="p-4 border-t border-white/10 flex items-center justify-between text-sm text-white/60">
        <div className="flex items-center gap-4">
          {currentPair.before && (
            <span>Before: {currentPair.before.uploader_name} • {new Date(currentPair.before.created_at).toLocaleString()}</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {currentPair.after && (
            <span>After: {currentPair.after.uploader_name} • {new Date(currentPair.after.created_at).toLocaleString()}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default BeforeAfterComparison;
